import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { LayoutDashboard, FileText, Mail, Package, LogOut, Search, Trash2, Pencil, Plus, Download } from "lucide-react";
import { toast } from "sonner";
import { api, euro } from "@/lib/api";
import { categorie, site } from "@/config/site";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATI = ["Nuova", "Contattato", "Chiusa"];
const CONTACT_STATI = ["Nuova", "Letta", "Gestita"];
const COLORS = ["#0A192F", "#C5A880", "#4B5563"];
const emptyProduct = () => ({
  categoria: "bare", nome: "", descrizione: "", immagine: "", prezzo: "", materiale: "", finitura: "",
  varianti: [], opzioni: [], disponibilita: true,
});

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.login({ email, password });
      localStorage.setItem("sm_token", r.token);
      onLogin();
    } catch { toast.error("Credenziali non valide"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm bg-card border border-border rounded-2xl p-8">
        <h1 className="text-3xl font-serif text-primary">Area riservata</h1>
        <p className="text-sm text-muted-foreground mt-1">Accesso amministratore</p>
        <input placeholder="Email" type="email" required data-testid="login-email" value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-6 w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent" />
        <input placeholder="Password" type="password" required data-testid="login-password" value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-4 w-full border border-border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent" />
        <button disabled={loading} data-testid="login-submit"
          className="mt-6 w-full bg-primary text-primary-foreground py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
          {loading ? "Accesso..." : "Accedi"}
        </button>
        <Link to="/" className="block text-center text-sm text-muted-foreground mt-4 hover:text-primary">← Torna al sito</Link>
      </form>
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div className="bg-card border border-border rounded-xl p-6">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="text-3xl font-serif text-primary mt-1">{value}</p>
  </div>
);

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [qFilter, setQFilter] = useState({ stato: "", q: "" });
  const [pQuery, setPQuery] = useState("");
  const [openQuote, setOpenQuote] = useState(null);
  const [productForm, setProductForm] = useState(null);
  const [savingProduct, setSavingProduct] = useState(false);

  const loadStats = () => api.stats().then(setStats).catch(() => onLogout());
  const loadQuotes = () => api.adminQuotes({ stato: qFilter.stato || undefined, q: qFilter.q || undefined }).then(setQuotes);
  useEffect(() => { loadStats(); }, []); // eslint-disable-line
  useEffect(() => { if (tab === "quotes") loadQuotes(); }, [tab, qFilter]); // eslint-disable-line
  useEffect(() => { if (tab === "contacts") api.adminContacts().then(setContacts); }, [tab]);
  useEffect(() => { if (tab === "catalog") api.catalog(undefined, pQuery || undefined).then(setProducts); }, [tab, pQuery]);

  const changeStatus = async (id, stato) => {
    await api.setStatus(id, stato);
    setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, stato } : q)));
    toast.success("Stato aggiornato");
    loadStats();
  };
  const delProduct = async (id) => {
    if (!window.confirm("Eliminare questo prodotto dal catalogo?")) return;
    await api.deleteProduct(id);
    setProducts((ps) => ps.filter((p) => p.id !== id));
    toast.success("Prodotto eliminato");
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!productForm?.nome) return toast.error("Inserisca il nome");
    setSavingProduct(true);
    try {
      const payload = {
        ...productForm,
        prezzo: Number(productForm.prezzo) || 0,
        id: productForm.id,
      };
      if (payload.id) {
        const updated = await api.updateProduct(payload.id, payload);
        setProducts((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
        toast.success("Prodotto aggiornato");
      } else {
        const { id, ...rest } = payload;
        const created = await api.createProduct(rest);
        setProducts((ps) => [created, ...ps]);
        toast.success("Prodotto creato");
      }
      setProductForm(null);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSavingProduct(false); }
  };

  const changeContactStatus = async (id, stato) => {
    await api.setContactStatus(id, stato);
    setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, stato } : c)));
    toast.success("Stato aggiornato");
  };

  const chartData = stats ? STATI.map((s) => ({ name: s, value: stats.by_status?.[s] || 0 })) : [];
  const nav = [
    { k: "stats", label: "Statistiche", icon: LayoutDashboard },
    { k: "quotes", label: "Preventivi", icon: FileText },
    { k: "contacts", label: "Contatti", icon: Mail },
    { k: "catalog", label: "Catalogo", icon: Package },
  ];

  return (
    <div className="min-h-screen bg-secondary flex" data-testid="admin-dashboard">
      <aside className="w-16 md:w-60 bg-primary text-primary-foreground flex flex-col shrink-0">
        <div className="p-5 hidden md:block"><span className="font-serif text-xl">{site.nomeBreve}</span><p className="text-xs text-white/50">Pannello admin</p></div>
        <nav className="flex-1 mt-4 space-y-1 px-2">
          {nav.map((n) => (
            <button key={n.k} data-testid={`tab-${n.k}`} onClick={() => setTab(n.k)}
              className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm transition-colors ${tab === n.k ? "bg-white/10 text-accent" : "text-white/70 hover:bg-white/5"}`}>
              <n.icon size={18} /> <span className="hidden md:inline">{n.label}</span>
            </button>
          ))}
        </nav>
        <button onClick={onLogout} data-testid="logout" className="m-2 flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-white/70 hover:bg-white/5">
          <LogOut size={18} /> <span className="hidden md:inline">Esci</span>
        </button>
      </aside>

      <main className="flex-1 p-5 md:p-10 overflow-auto">
        {tab === "stats" && stats && (
          <div>
            <h1 className="text-3xl font-serif text-primary mb-6">Statistiche</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Preventivi" value={stats.quotes_count} />
              <Stat label="Contatti" value={stats.contacts_count} />
              <Stat label="Prodotti" value={stats.products_count} />
              <Stat label="Valore potenziale" value={euro(stats.revenue_potential)} />
            </div>
            <div className="mt-6 grid lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl p-6">
                <p className="font-serif text-xl text-primary mb-4">Preventivi per stato</p>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{chartData.map((e, i) => <Cell key={i} fill={COLORS[i]} />)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-card border border-border rounded-xl p-6">
                <p className="font-serif text-xl text-primary mb-4">Ultimi preventivi</p>
                <div className="space-y-3">
                  {stats.recent.map((q) => (
                    <div key={q.id} className="flex justify-between text-sm border-b border-border pb-2">
                      <span className="text-primary">{q.cliente_nome || "—"}</span>
                      <span className="text-muted-foreground">{euro(q.totale)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "quotes" && (
          <div>
            <h1 className="text-3xl font-serif text-primary mb-6">Preventivi</h1>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <input placeholder="Cerca cliente" data-testid="quotes-search" value={qFilter.q}
                  onChange={(e) => setQFilter({ ...qFilter, q: e.target.value })}
                  className="pl-9 pr-4 py-2.5 border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
              <select data-testid="quotes-filter" value={qFilter.stato} onChange={(e) => setQFilter({ ...qFilter, stato: e.target.value })}
                className="px-4 py-2.5 border border-border rounded-lg bg-card">
                <option value="">Tutti gli stati</option>{STATI.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border">
                  <th className="p-4">Rif.</th><th className="p-4">Cliente</th><th className="p-4">Tipologia</th><th className="p-4">Totale</th><th className="p-4">Data</th><th className="p-4">Stato</th>
                </tr></thead>
                <tbody>
                  {quotes.map((q) => (
                    <tr key={q.id} className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/60" data-testid={`quote-row-${q.id}`}
                      onClick={() => setOpenQuote(q)}>
                      <td className="p-4 font-mono text-xs">#{q.id.slice(0, 8)}</td>
                      <td className="p-4 text-primary">{q.cliente_nome || "—"}<div className="text-xs text-muted-foreground">{q.cliente_telefono}</div></td>
                      <td className="p-4">{q.tipologia}</td>
                      <td className="p-4">{euro(q.totale)}</td>
                      <td className="p-4 text-muted-foreground">{(q.created_at || "").slice(0, 10)}</td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <select value={q.stato} data-testid={`quote-status-${q.id}`} onChange={(e) => changeStatus(q.id, e.target.value)}
                          className="border border-border rounded-lg px-2 py-1.5 bg-background">
                          {STATI.map((s) => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {quotes.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Nessun preventivo</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div>
            <h1 className="text-3xl font-serif text-primary mb-6">Messaggi di contatto</h1>
            <div className="grid md:grid-cols-2 gap-4">
              {contacts.map((c) => (
                <div key={c.id} className="bg-card border border-border rounded-xl p-5" data-testid={`contact-${c.id}`}>
                  <div className="flex justify-between gap-3">
                    <p className="font-semibold text-primary">{c.nome}</p>
                    <span className="text-xs text-muted-foreground">{(c.created_at || "").slice(0, 10)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{c.email} · {c.telefono}</p>
                  <p className="mt-3 text-sm text-primary/90">{c.messaggio}</p>
                  <select value={c.stato || "Nuova"} onChange={(e) => changeContactStatus(c.id, e.target.value)}
                    className="mt-3 border border-border rounded-lg px-2 py-1.5 bg-background text-sm">
                    {CONTACT_STATI.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              {contacts.length === 0 && <p className="text-muted-foreground">Nessun messaggio</p>}
            </div>
          </div>
        )}

        {tab === "catalog" && (
          <div>
            <h1 className="text-3xl font-serif text-primary mb-6">Gestione catalogo</h1>
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="relative max-w-xs flex-1 min-w-[12rem]">
                <Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
                <input placeholder="Cerca prodotto" data-testid="catalog-search" value={pQuery}
                  onChange={(e) => setPQuery(e.target.value)}
                  className="pl-9 pr-4 py-2.5 w-full border border-border rounded-lg bg-card focus:outline-none focus:ring-2 focus:ring-accent" />
              </div>
              <button type="button" onClick={() => setProductForm(emptyProduct())} data-testid="catalog-new"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2.5 rounded-full text-sm font-semibold">
                <Plus size={16} /> Nuovo prodotto
              </button>
            </div>
            <div className="bg-card border border-border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="p-4">Nome</th><th className="p-4">Categoria</th><th className="p-4">Prezzo</th><th className="p-4"></th></tr></thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-0" data-testid={`product-row-${p.id}`}>
                      <td className="p-4 text-primary">{p.nome}</td>
                      <td className="p-4">{categorie[p.categoria]?.label || p.categoria}</td>
                      <td className="p-4">{euro(p.prezzo)}</td>
                      <td className="p-4 text-right space-x-2">
                        <button type="button" onClick={() => setProductForm({
                          id: p.id, categoria: p.categoria, nome: p.nome, descrizione: p.descrizione || "",
                          immagine: p.immagine || "", prezzo: p.prezzo, materiale: p.materiale || "", finitura: p.finitura || "",
                          varianti: p.varianti || [], opzioni: p.opzioni || [], disponibilita: p.disponibilita !== false,
                        })} className="text-primary hover:opacity-70" data-testid={`edit-${p.id}`}><Pencil size={16} /></button>
                        <button onClick={() => delProduct(p.id)} data-testid={`del-${p.id}`} className="text-destructive hover:opacity-70"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <Dialog open={!!openQuote} onOpenChange={(o) => { if (!o) setOpenQuote(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="quote-detail">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Preventivo {openQuote ? `#${openQuote.id.slice(0, 8)}` : ""}</DialogTitle>
          </DialogHeader>
          {openQuote && (
            <div className="space-y-3 text-sm">
              <p><span className="text-muted-foreground">Cliente:</span> {openQuote.cliente_nome || "—"}</p>
              <p><span className="text-muted-foreground">Telefono:</span> {openQuote.cliente_telefono || "—"}</p>
              <p><span className="text-muted-foreground">Email:</span> {openQuote.cliente_email || "—"}</p>
              <p><span className="text-muted-foreground">Tipologia:</span> {openQuote.tipologia || "—"}</p>
              {openQuote.note && <p><span className="text-muted-foreground">Note:</span> {openQuote.note}</p>}
              <ul className="border border-border rounded-lg divide-y divide-border">
                {(openQuote.items || []).map((it, i) => (
                  <li key={i} className="flex justify-between px-3 py-2">
                    <span>{it.nome}</span>
                    <span>{euro(it.prezzo)}</span>
                  </li>
                ))}
              </ul>
              <p className="font-serif text-xl text-primary text-right">{euro(openQuote.totale)}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                <a href={api.pdfUrl(openQuote.id)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-border px-4 py-2 rounded-full">
                  <Download size={14} /> PDF
                </a>
                <a href={`/preventivo/${openQuote.id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 border border-border px-4 py-2 rounded-full">
                  Apri sul sito
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!productForm} onOpenChange={(o) => { if (!o) setProductForm(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{productForm?.id ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle>
          </DialogHeader>
          {productForm && (
            <form onSubmit={saveProduct} className="space-y-3" data-testid="product-form">
              <label className="block text-sm font-medium">Nome
                <input required value={productForm.nome} onChange={(e) => setProductForm({ ...productForm, nome: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">Categoria
                <select value={productForm.categoria} onChange={(e) => setProductForm({ ...productForm, categoria: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2">
                  {Object.entries(categorie).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </label>
              <label className="block text-sm font-medium">Prezzo (€)
                <input type="number" min="0" step="0.01" value={productForm.prezzo}
                  onChange={(e) => setProductForm({ ...productForm, prezzo: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">Descrizione
                <textarea rows={3} value={productForm.descrizione} onChange={(e) => setProductForm({ ...productForm, descrizione: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">Materiale
                <input value={productForm.materiale} onChange={(e) => setProductForm({ ...productForm, materiale: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">Finitura
                <input value={productForm.finitura} onChange={(e) => setProductForm({ ...productForm, finitura: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <label className="block text-sm font-medium">URL immagine
                <input value={productForm.immagine} onChange={(e) => setProductForm({ ...productForm, immagine: e.target.value })}
                  className="mt-1 w-full border border-border rounded-lg px-3 py-2" />
              </label>
              <div className="flex gap-2 pt-2">
                <button type="submit" disabled={savingProduct} className="bg-primary text-primary-foreground px-5 py-2 rounded-full font-semibold">
                  {savingProduct ? "Salvataggio..." : "Salva"}
                </button>
                <button type="button" onClick={() => setProductForm(null)} className="px-5 py-2 rounded-full border border-border">Annulla</button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(!!localStorage.getItem("sm_token"));
  const logout = () => { localStorage.removeItem("sm_token"); setAuth(false); };
  return auth ? <Dashboard onLogout={logout} /> : <Login onLogin={() => setAuth(true)} />;
}
